import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbar } from '@angular/material/toolbar';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, map, of, Subject, takeUntil } from 'rxjs';
import { TrackerConfigTool } from 'src/app/classes/shared/tracker-config.tool';
import { TrackerTemplateEditor } from 'src/app/classes/shared/tracker-template-editor';
import { Pop } from 'src/app/classes/shared/pop';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { ButtonComponent } from 'ui-library';
import { TrackerConfigFormComponent } from '../../resources/tracker-config-form/tracker-config-form.component';

// Applies ONE set of values, edited once here, to every tracker passed in - the web equivalent
// of the mobile app's "configuration multiple" (list-update-kyds.page.ts). No name - each
// tracker gets these values written into its own settings, and is linked to the template they
// were prefilled from, if any (see TrackerConfigTool.saveSettings()). Saving as a reusable
// template is a deliberately separate action ("Enregistrer comme modèle") - see
// onSaveAsTemplate() - and never applies anything to the trackers themselves.
@Component({
  selector: 'app-kyd-bulk-config-dialog',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatToolbar,
    ButtonComponent,
    TrackerConfigFormComponent,
  ],
  standalone: true,
  templateUrl: './kyd-bulk-config-dialog.component.html',
  styleUrls: ['./kyd-bulk-config-dialog.component.scss']
})
export class KydBulkConfigDialogComponent implements OnInit, OnDestroy {
  private readonly kydConfigTool: TrackerConfigTool = inject(TrackerConfigTool);
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly pop: Pop = inject(Pop);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly data: { trackers: Tracker[] } = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<KydBulkConfigDialogComponent> = inject(MatDialogRef<KydBulkConfigDialogComponent>);

  private readonly destroy$ = new Subject<void>();

  trackers: Tracker[] = this.data.trackers;
  // The caller only offers this dialog for a uniform-type selection, so any tracker's type is
  // representative of the whole batch. Starts from defaults rather than any one tracker's values.
  readonly type: NonNullable<TrackerConfig['type']> = (this.trackers[0]?.type as TrackerConfig['type']) ?? 'Unit';
  readonly editor = new TrackerTemplateEditor(this.type, this.kydConfigTool.mapNestedToFlat(null, this.type));
  saving: boolean = false;

  ngOnInit() {
    this.trackerService.getConfigs().pipe(takeUntil(this.destroy$)).subscribe((configs: TrackerConfig[]) => {
      this.editor.configs = configs || [];
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  save() {
    this.editor.save(() => this.applyToAllTrackers());
  }

  // Saving as a template alone never touches the trackers - offers to apply it right away.
  onSaveAsTemplate() {
    this.editor.saveAsTemplate(() =>
      this.pop.confirm(this.translate.instant('trackers.apply_template_to_sensor_confirm'), (confirm: any) => {
        if (confirm.ok) this.applyToAllTrackers();
      })
    );
  }

  private applyToAllTrackers() {
    const { settings, offlineDelay } = this.editor.draft;
    const configId = this.editor.appliedTemplateId;
    this.saving = true;

    const applies = this.trackers.map(tracker =>
      this.kydConfigTool.saveSettings(tracker, settings, offlineDelay, configId).pipe(
        map(() => true),
        catchError(err => { console.error('[KYD bulk config] failed for', tracker.name, err); return of(false); })
      )
    );

    forkJoin(applies).subscribe(results => {
      this.saving = false;
      const failedCount = results.filter(r => !r).length;

      if (failedCount === 0) {
        this.pop.toast(this.translate.instant('units.device.bulk_config_saved', { count: results.length }), 'success', 4000, 'save');
      } else if (failedCount < results.length) {
        this.pop.warning(this.translate.instant('trackers.error.some_kyd_config_failed', { failed: failedCount, total: results.length }));
      } else {
        this.pop.error(this.translate.instant('trackers.error.config_save'));
      }
      this.dialogRef.close(true);
    });
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
